import React, { useMemo, useState } from "react";
import moment from "moment";
import {
  Box,
  Button,
  CircularProgress,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import { Link } from "react-router-dom";
import mappedIcon from "assets/images/mappedicon.svg";
import { AuditDetailItem } from "models";
import type { AuditNoteEntry, MovedToBackstockItem } from "models/audit/AuditDetailModel";
import type { DailyCashierShiftNote } from "../../utils/cashierShiftNotes";
import {
  AuditDashboardScratcherTab,
  calculateDefaultTotals,
  calculateMovedTotals,
  formatBackOnSaleTimestamp,
  formatCurrency,
  getBackOnSaleDestination,
  getBackstockLocationLabel,
  getEligibilityKey,
  getMovedQty,
  getMovedRange,
  getMovedValue,
  getTicketDisplay,
  groupScratchRows,
} from "../../utils";
import { auditDashboardTheme } from "../../theme";
import type { ManualSoldOutEligibilityResult } from "services/scratcherService";
import {
  getScratcherDividerSx as getDividerSx,
  scratcherBodyCellSx as bodyCellSx,
  scratcherEmptyStateCellSx as emptyStateCellSx,
  scratcherDollarCellSx as dollarCellSx,
  scratcherGameMetaSx as gameMetaSx,
  scratcherGameTextSx as gameTextSx,
  scratcherHeaderCellSx as headerCellSx,
  scratcherHoverBackground,
  scratcherHoverRowSx as hoverRowSx,
  scratcherLinkedTextSx as linkedTextSx,
  scratcherManualSoldChipSx as manualSoldChipSx,
  scratcherMetricCountCellSx as metricCountCellSx,
  scratcherMetricCellSx as metricCellSx,
  scratcherTableContainerSx,
  scratcherTableColumnWidths as columnWidths,
  scratcherTableSx,
  scratcherTotalCellSx as totalCellSx,
  scratcherTotalLabelSx as totalLabelSx,
  scratcherTotalPackCountSx as totalPackCountSx,
  scratcherVarianceCountCellSx as varianceCountCellSx,
} from "./AuditDetailScratcherTable.styles";

interface AuditDetailScratcherTableProps {
  activeTab: AuditDashboardScratcherTab;
  scratcherCount: number;
  mismatchedCount: number;
  movedCount: number;
  defaultRows: AuditDetailItem[];
  movedRows: MovedToBackstockItem[];
  hasPosType: boolean;
  isLoading?: boolean;
  totalPacksCount: number;
  auditNotes?: AuditNoteEntry[];
  cashierShiftNotes?: DailyCashierShiftNote[];
  onTabChange: (event: React.SyntheticEvent, nextTab: AuditDashboardScratcherTab) => void;
  manualSoldOutEligibility?: ManualSoldOutEligibilityResult[];
  isManualSoldOutEligibilityLoading?: boolean;
  markingSoldOutKey?: string | null;
  storeId?: string | null;
  onMarkMovedPackSoldOut?: (item: MovedToBackstockItem) => void | Promise<void>;
}

interface ScratcherLinkTextProps {
  to?: string | null;
  children: React.ReactNode;
  sx: SxProps<Theme>;
  title?: string;
  ariaHidden?: boolean;
}

function ScratcherLinkText({ to, children, sx, title, ariaHidden }: ScratcherLinkTextProps) {
  if (!to) {
    return (
      <Box component="span" sx={sx} title={title} aria-hidden={ariaHidden}>
        {children}
      </Box>
    );
  }

  return (
    <Box component={Link} to={to} sx={sx} title={title} aria-hidden={ariaHidden}>
      {children}
    </Box>
  );
}

const getGameLifecyclePath = (
  storeId: string | null | undefined,
  gameId: string | number | null | undefined
) => {
  if (!storeId || !gameId) {
    return null;
  }

  return `/store/${storeId}/scratcher/${gameId}/packlifecycle`;
};

const getPackLifecyclePath = (
  storeId: string | null | undefined,
  gameId: string | number | null | undefined,
  packId: string | number | null | undefined
) => {
  if (!storeId || !gameId || !packId || packId === "UNKNOWN_PACK") {
    return null;
  }

  return `/store/${storeId}/scratcher/${gameId}/packlifecycle/${packId}?sold_out=true`;
};

function VarianceValue({ value, currency = false }: { value: number; currency?: boolean }) {
  if (value === 0) {
    return (
      <Box
        component="img"
        src={mappedIcon}
        alt="Matched"
        aria-label="No variance"
        sx={{
          display: "inline-block",
          width: 18,
          height: 18,
          verticalAlign: "middle",
        }}
      />
    );
  }

  const prefix = value > 0 ? "+" : "-";
  const displayValue = currency ? `${prefix}${formatCurrency(Math.abs(value))}` : `${value > 0 ? "+" : ""}${value}`;

  return (
    <Typography component="span" sx={{ fontSize: "inherit", fontWeight: "inherit", color: value > 0 ? "#0000ff" : "#ff0000" }}>
      {displayValue}
    </Typography>
  );
}

function MetricCountValue({ children }: { children: React.ReactNode }) {
  return (
    <Box component="span" className="audit-scratcher-count-value">
      {children}
    </Box>
  );
}

const formatAuditNoteTimestamp = (entry: AuditNoteEntry): string | null => {
  const rawValue = entry.created_at_local || entry.created_at;
  if (!rawValue) return null;

  const parsed = moment.parseZone(rawValue);
  return parsed.isValid() ? parsed.format("MM/DD/YYYY h:mm A") : rawValue;
};

function AuditNotesFootnote({
  notes,
  cashierShiftNotes,
  colSpan,
}: {
  notes: AuditNoteEntry[];
  cashierShiftNotes: DailyCashierShiftNote[];
  colSpan: number;
}) {
  const visibleNotes = [
    ...cashierShiftNotes.map((entry) => ({
      key: entry.key,
      text: entry.text,
      author: entry.authorName,
      timestamp: entry.createdAt
        ? formatAuditNoteTimestamp({ created_at: entry.createdAt })
        : null,
      shiftLabel: entry.shiftLabel,
      shiftTimeRange: entry.shiftTimeRange,
    })),
    ...notes
      .filter((entry) => entry.text?.trim())
      .map((entry, index) => ({
        key: entry.id || `${entry.created_at || "note"}-${index}`,
        text: entry.text || "",
        author: entry.author_name?.trim() || "",
        timestamp: formatAuditNoteTimestamp(entry),
        shiftLabel: "",
        shiftTimeRange: "",
      })),
  ];
  if (visibleNotes.length === 0) return null;

  return (
    <TableRow>
      <TableCell
        colSpan={colSpan}
        sx={{
          px: "12px !important",
          py: "10px !important",
          borderTop: `1px solid ${auditDashboardTheme.colors.lineSoft}`,
          bgcolor: "#FFFBEB",
          color: auditDashboardTheme.colors.textMutedStrong,
          whiteSpace: "normal",
          "&&": {
            textAlign: "left !important",
          },
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", textAlign: "left", width: "100%" }}>
          <Typography sx={{ fontSize: 14, lineHeight: 1.35, fontWeight: 800, color: "#92400E" }}>
            Cashier Notes
          </Typography>
          {visibleNotes.map((entry) => {
            return (
              <Box
                key={entry.key}
                sx={{
                  width: "100%",
                  borderTop: "1px solid #FDE68A",
                  pt: 1,
                  mt: 1,
                }}
              >
                {entry.shiftLabel || entry.shiftTimeRange ? (
                  <Typography sx={{ mb: 0.35, fontSize: 12, lineHeight: 1.35, fontWeight: 700, color: "#92400E", textAlign: "left" }}>
                    {[entry.shiftLabel, entry.shiftTimeRange].filter(Boolean).join(" | ")}
                  </Typography>
                ) : null}
                <Typography sx={{ fontSize: 14, lineHeight: 1.45, color: auditDashboardTheme.colors.text, whiteSpace: "pre-wrap", textAlign: "left" }}>
                  {entry.text}
                </Typography>
                {(entry.author || entry.timestamp) ? (
                  <Typography sx={{ mt: 0.35, fontSize: 12, lineHeight: 1.35, color: auditDashboardTheme.colors.textMutedStrong, textAlign: "left" }}>
                    {[entry.author, entry.timestamp].filter(Boolean).join(" - ")}
                  </Typography>
                ) : null}
              </Box>
            );
          })}
        </Box>
      </TableCell>
    </TableRow>
  );
}

export default function AuditDetailScratcherTable({
  activeTab,
  scratcherCount,
  mismatchedCount,
  movedCount,
  defaultRows,
  movedRows,
  hasPosType,
  isLoading = false,
  totalPacksCount,
  auditNotes = [],
  cashierShiftNotes = [],
  onTabChange,
  manualSoldOutEligibility = [],
  isManualSoldOutEligibilityLoading = false,
  markingSoldOutKey = null,
  storeId,
  onMarkMovedPackSoldOut,
}: AuditDetailScratcherTableProps) {
  const isMovedToBackstockView = activeTab === "moved_to_backstock";
  const [hoveredGroupKey, setHoveredGroupKey] = useState<string | null>(null);

  const groupedRows = useMemo(() => groupScratchRows(defaultRows, hasPosType), [defaultRows, hasPosType]);
  const totals = useMemo(
    () => (isMovedToBackstockView ? calculateMovedTotals(movedRows) : calculateDefaultTotals(defaultRows)),
    [defaultRows, isMovedToBackstockView, movedRows]
  );

  const manualSoldOutEligibilityByKey = useMemo(() => {
    const map = new Map<string, ManualSoldOutEligibilityResult>();
    manualSoldOutEligibility.forEach((item) => {
      map.set(getEligibilityKey(item.game_id, item.pack_id), item);
    });
    return map;
  }, [manualSoldOutEligibility]);

  const defaultColSpan = hasPosType ? 9 : 5;
  const activeColSpan = isMovedToBackstockView ? 6 : defaultColSpan;
  const movedColumnWidths = {
    game: "24%",
    pack: "23%",
    range: "16%",
    qty: "8%",
    value: "11%",
    currentStatus: "18%",
  };
  const gameDividerColor = "rgba(200, 206, 227, 0.2)";

  return (
    <TableContainer sx={scratcherTableContainerSx}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "16px",
          flexWrap: "wrap",
          px: "12px",
          pt: "10px",
          pb: 0,
          borderBottom: "1px solid",
          borderColor: auditDashboardTheme.colors.lineSoft,
          bgcolor: auditDashboardTheme.colors.surface,
        }}
      >
        <Typography
          component="div"
          sx={{
            color: auditDashboardTheme.colors.textMutedStrong,
            fontSize: 13,
            fontWeight: 600,
            lineHeight: 1.2,
            whiteSpace: "nowrap",
          }}
        >
          {`Scratcher(${scratcherCount.toLocaleString()})`}
        </Typography>
        <Tabs
          value={activeTab}
          onChange={onTabChange}
          sx={{
            ml: "auto",
            minHeight: 0,
            alignSelf: "flex-end",
            "& .MuiTabs-flexContainer": {
              gap: "4px",
              alignItems: "flex-end",
              flexWrap: "wrap",
            },
            "& .MuiTabs-indicator": { display: "none" },
            "& .MuiTab-root": {
              minHeight: 28,
              minWidth: 0,
              px: "10px",
              py: "2px",
              color: auditDashboardTheme.colors.textMutedStrong,
              fontSize: 14,
              fontWeight: 600,
              lineHeight: 1.2,
              textTransform: "none",
              borderRadius: "8px 8px 0 0",
              border: `1px solid ${auditDashboardTheme.colors.line}`,
              borderBottom: 0,
              bgcolor: auditDashboardTheme.colors.surface,
              mb: "-1px",
            },
            "& .Mui-selected": {
              color: `${auditDashboardTheme.colors.blue} !important`,
              borderColor: auditDashboardTheme.colors.blue,
              bgcolor: auditDashboardTheme.colors.surface,
              fontWeight: 600,
            },
          }}
        >
          <Tab label="All" value="all" />
          {hasPosType ? <Tab label={`Mismatched (${mismatchedCount})`} value="mismatched" /> : null}
          <Tab label={`Moved to Backstock (${movedCount})`} value="moved_to_backstock" />
        </Tabs>
      </Box>
      <Table
        size="small"
        sx={{
          ...scratcherTableSx,
          width: "100%",
          minWidth: 0,
        }}
      >
        <colgroup>
          {isMovedToBackstockView ? (
            <>
              <col style={{ width: movedColumnWidths.game }} />
              <col style={{ width: movedColumnWidths.pack }} />
              <col style={{ width: movedColumnWidths.range }} />
              <col style={{ width: movedColumnWidths.qty }} />
              <col style={{ width: movedColumnWidths.value }} />
              <col style={{ width: movedColumnWidths.currentStatus }} />
            </>
          ) : (
            <>
              <col style={{ width: columnWidths.game }} />
              <col style={{ width: columnWidths.pack }} />
              <col style={{ width: columnWidths.range }} />
              <col style={{ width: columnWidths.qty }} />
              <col style={{ width: columnWidths.value }} />
              {hasPosType ? (
                <>
                  <col style={{ width: columnWidths.qty }} />
                  <col style={{ width: columnWidths.value }} />
                  <col style={{ width: columnWidths.varianceQty }} />
                  <col style={{ width: columnWidths.varianceValue }} />
                </>
              ) : null}
            </>
          )}
        </colgroup>
        <TableHead>
          {isMovedToBackstockView ? (
            <TableRow>
              {["Game", "Pack", "Range", "Qty", "Value", "Current Status"].map((label, index, arr) => {
                const width =
                  index === 0
                    ? movedColumnWidths.game
                    : index === 1
                      ? movedColumnWidths.pack
                      : index === 2
                        ? movedColumnWidths.range
                        : index === 3
                          ? movedColumnWidths.qty
                          : index === 4
                            ? movedColumnWidths.value
                            : movedColumnWidths.currentStatus;

                return (
                  <TableCell
                    key={label}
                    sx={{
                      ...headerCellSx,
                      width,
                      textAlign: index === 0 || index === 1 || index === 5 ? "left" : "right",
                      ...getDividerSx(index === arr.length - 1),
                    }}
                  >
                    {label}
                  </TableCell>
                );
              })}
            </TableRow>
          ) : (
              <TableRow>
                <TableCell sx={{ ...headerCellSx, width: columnWidths.game, textAlign: "left", ...getDividerSx(false) }}>
                  Game
                </TableCell>
                <TableCell sx={{ ...headerCellSx, width: columnWidths.pack, textAlign: "left", ...getDividerSx(false) }}>
                  Pack
                </TableCell>
                <TableCell sx={{ ...headerCellSx, width: columnWidths.range, textAlign: "right", ...getDividerSx(false) }}>
                  Range
                </TableCell>
                <TableCell colSpan={2} sx={{ ...headerCellSx, textAlign: "right", ...getDividerSx(false) }}>
                  Sales Expected
                </TableCell>
                {hasPosType ? (
                  <>
                    <TableCell colSpan={2} sx={{ ...headerCellSx, textAlign: "right", ...getDividerSx(false) }}>
                      Sales Reported
                    </TableCell>
                    <TableCell colSpan={2} sx={{ ...headerCellSx, textAlign: "right", ...getDividerSx(true) }}>
                      Variance
                    </TableCell>
                  </>
                ) : null}
              </TableRow>
          )}
        </TableHead>

        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={activeColSpan}>
                <Box sx={{ py: 5, display: "flex", justifyContent: "center" }}>
                  <CircularProgress size={28} />
                </Box>
              </TableCell>
            </TableRow>
          ) : isMovedToBackstockView ? (
            movedRows.length ? (
              movedRows.map((item, rowIndex) => {
                const eligibility = manualSoldOutEligibilityByKey.get(getEligibilityKey(item.gameId, item.packId));
                const isEligibilityLoading = isManualSoldOutEligibilityLoading && !eligibility;
                const canMarkRowSoldOut = Boolean(
                  onMarkMovedPackSoldOut && item.gameId && item.packId && eligibility?.can_mark_manual_sold_out
                );
                const rowSoldOutKey = `${item.gameId}-${item.packId}`;
                const isMarkingRowSoldOut = markingSoldOutKey === rowSoldOutKey;
                const returnedToCounterAt = eligibility?.returned_to_counter_at ?? null;
                const backOnSaleDestination = getBackOnSaleDestination(eligibility?.current_subgroup, Boolean(returnedToCounterAt));
                const backstockLocationLabel = getBackstockLocationLabel(eligibility?.current_subgroup);
                const backOnSaleTimestamp = formatBackOnSaleTimestamp(returnedToCounterAt);
                const rowDividerSx = { borderBottom: `1px solid ${gameDividerColor} !important` };
                const ineligibleLabel = returnedToCounterAt
                  ? `Back on Sale${backOnSaleDestination ? `: ${backOnSaleDestination}` : ""} ${backOnSaleTimestamp}`
                  : eligibility?.message || "Not eligible";

                return (
                  <TableRow key={item.id || rowIndex} sx={hoverRowSx}>
                    <TableCell
                      align="left"
                      sx={{
                        ...bodyCellSx,
                        width: movedColumnWidths.game,
                        textAlign: "left !important",
                        ...rowDividerSx,
                        ...getDividerSx(false),
                      }}
                    >
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", width: "100%", gap: 0.25 }}>
                        <Box sx={{ width: "100%" }}>
                          <ScratcherLinkText to={getGameLifecyclePath(storeId, item.gameId)} sx={gameTextSx}>
                            {item.gameName}
                          </ScratcherLinkText>
                          <Box sx={gameMetaSx}>
                            <span>{formatCurrency(item.costPerTicket)}</span>
                            <span>{item.plu}</span>
                          </Box>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{
                        ...bodyCellSx,
                        width: movedColumnWidths.pack,
                        textAlign: "left !important",
                        ...rowDividerSx,
                        ...getDividerSx(false),
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-start",
                          gap: 0.5,
                          width: "100%",
                          minWidth: 0,
                          whiteSpace: "nowrap",
                        }}
                      >
                        <ScratcherLinkText to={getGameLifecyclePath(storeId, item.gameId)} sx={linkedTextSx}>
                          {item.gameId}
                        </ScratcherLinkText>
                        <Box component="span" sx={{ color: "text.secondary" }}>
                          -
                        </Box>
                        <ScratcherLinkText to={getPackLifecyclePath(storeId, item.gameId, item.packId)} sx={linkedTextSx}>
                          {item.packId}
                        </ScratcherLinkText>
                      </Box>
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        ...bodyCellSx,
                        width: movedColumnWidths.range,
                        textAlign: "right !important",
                        fontSize: 13,
                        fontWeight: 400,
                        ...rowDividerSx,
                        ...getDividerSx(false),
                      }}
                    >
                      <Box sx={{ width: "100%", textAlign: "right", wordBreak: "break-word" }}>{getMovedRange(item)}</Box>
                    </TableCell>
                    <TableCell align="right" sx={{ ...bodyCellSx, width: movedColumnWidths.qty, ...rowDividerSx, ...getDividerSx(false) }}>
                      {getMovedQty(item)}
                    </TableCell>
                    <TableCell align="right" sx={{ ...bodyCellSx, ...dollarCellSx, width: movedColumnWidths.value, ...rowDividerSx, ...getDividerSx(false) }}>
                      {formatCurrency(getMovedValue(item))}
                    </TableCell>
                    <TableCell
                      sx={{
                        ...bodyCellSx,
                        width: movedColumnWidths.currentStatus,
                        whiteSpace: "normal",
                        textAlign: "left !important",
                        ...rowDividerSx,
                        ...getDividerSx(true),
                      }}
                    >
                      {isEligibilityLoading ? (
                        <CircularProgress size={16} />
                      ) : canMarkRowSoldOut ? (
                        <Tooltip title="Mark this pack sold out as a correction">
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "flex-start",
                              gap: 0.15,
                              minWidth: 0,
                              width: "100%",
                              textAlign: "left",
                            }}
                          >
                            <Box component="span" sx={{ fontSize: 12, color: "text.secondary", lineHeight: 1.2, textAlign: "left" }}>
                              {`In Backstock${backstockLocationLabel ? `: ${backstockLocationLabel}` : ""}`}
                            </Box>
                            <Button
                              size="small"
                              variant="text"
                              disabled={Boolean(markingSoldOutKey)}
                              onClick={() => onMarkMovedPackSoldOut?.(item)}
                              sx={{ minWidth: 0, px: 0, py: 0, fontSize: 12, lineHeight: 1.2, textTransform: "none" }}
                            >
                              {isMarkingRowSoldOut ? <CircularProgress size={14} color="inherit" /> : "Mark Sold Out"}
                            </Button>
                          </Box>
                        </Tooltip>
                      ) : (
                        <Tooltip title={eligibility?.message || "Pack is not currently eligible to mark sold out"}>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "flex-start",
                              gap: 0.15,
                              minWidth: 0,
                              width: "100%",
                              textAlign: "left",
                            }}
                          >
                            <Box component="span" sx={{ color: "text.secondary", fontSize: 12, lineHeight: 1.2, textAlign: "left" }}>
                              {returnedToCounterAt
                                ? `Back on Sale${backOnSaleDestination ? `: ${backOnSaleDestination}` : ""}`
                                : ineligibleLabel}
                            </Box>
                            {returnedToCounterAt ? (
                              <Box component="span" sx={{ color: "text.secondary", fontSize: 12, lineHeight: 1.2, textAlign: "left" }}>
                                {backOnSaleTimestamp}
                              </Box>
                            ) : null}
                          </Box>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell
                  colSpan={activeColSpan}
                  align="center"
                  sx={emptyStateCellSx}
                >
                  <Box sx={{ minHeight: 80, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    No moved to backstock data found.
                  </Box>
                </TableCell>
              </TableRow>
            )
          ) : groupedRows.length ? (
            groupedRows.map(({ item, isFirstInGroup, isLastInGroup, groupSize, rowColor }) => {
              const hasNoPack = !item.packId;
              const isUnknownPack = item.packId === "UNKNOWN_PACK";
              const showGameIdInPackCell = isFirstInGroup || groupSize === 1;
              const groupKey = item.gameId || item.id;
              const isGroupHovered = hoveredGroupKey === groupKey;
              const rowBackgroundColor = isGroupHovered ? scratcherHoverBackground : rowColor || "transparent";
              const groupDividerSx = isLastInGroup ? { borderBottom: `1px solid ${gameDividerColor} !important` } : {};
              const rowSpanDividerSx = { borderBottom: `1px solid ${gameDividerColor} !important` };

              return (
                <TableRow
                  key={item.id}
                  onMouseEnter={() => setHoveredGroupKey(groupKey)}
                  onMouseLeave={() => setHoveredGroupKey(null)}
                  sx={{
                    backgroundColor: rowBackgroundColor,
                    "& > td, & > th": {
                      backgroundColor: `${rowBackgroundColor} !important`,
                    },
                  }}
                >
                  {isFirstInGroup ? (
                    <TableCell
                      rowSpan={groupSize}
                      align="left"
                      sx={{
                        ...bodyCellSx,
                        width: columnWidths.game,
                        textAlign: "left !important",
                        ...rowSpanDividerSx,
                        ...getDividerSx(false),
                      }}
                    >
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", width: "100%", gap: 0.25 }}>
                        <Box sx={{ width: "100%" }}>
                          <ScratcherLinkText to={getGameLifecyclePath(storeId, item.gameId)} sx={gameTextSx}>
                            {item.gameName}
                          </ScratcherLinkText>
                          <Box sx={gameMetaSx}>
                            <span>{formatCurrency(item.costPerTicket)}</span>
                            <span>{item.plu}</span>
                          </Box>
                        </Box>
                      </Box>
                    </TableCell>
                  ) : null}
                  <TableCell
                    align="left"
                    sx={{
                      ...bodyCellSx,
                      width: columnWidths.pack,
                      textAlign: "left !important",
                      ...groupDividerSx,
                      ...getDividerSx(false),
                    }}
                  >
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", width: "100%" }}>
                      {hasNoPack ? (
                        <ScratcherLinkText to={getGameLifecyclePath(storeId, item.gameId)} sx={linkedTextSx}>
                          {item.gameId}
                        </ScratcherLinkText>
                      ) : (
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "max-content 8px max-content",
                            alignItems: "center",
                            justifyContent: "flex-start",
                            columnGap: "4px",
                            width: "100%",
                            minWidth: 0,
                            whiteSpace: "nowrap",
                          }}
                        >
                          <ScratcherLinkText
                            to={isUnknownPack ? null : getGameLifecyclePath(storeId, item.gameId)}
                            ariaHidden={!showGameIdInPackCell}
                            sx={{
                              ...linkedTextSx,
                              visibility: showGameIdInPackCell ? "visible" : "hidden",
                              pointerEvents: showGameIdInPackCell ? "auto" : "none",
                            }}
                          >
                            {item.gameId}
                          </ScratcherLinkText>
                          <Box
                            component="span"
                            aria-hidden={!showGameIdInPackCell}
                            sx={{
                              color: "text.secondary",
                              visibility: showGameIdInPackCell ? "visible" : "hidden",
                            }}
                          >
                            -
                          </Box>
                          <ScratcherLinkText
                            to={isUnknownPack ? null : getPackLifecyclePath(storeId, item.gameId, item.packId)}
                            sx={linkedTextSx}
                            title={isUnknownPack ? item.validationMessage : undefined}
                          >
                            {item.packId}
                          </ScratcherLinkText>
                        </Box>
                      )}
                      {item.isManualSoldOut ? (
                        <Box component="span" sx={manualSoldChipSx}>
                          Manual Sold Out
                        </Box>
                      ) : null}
                    </Box>
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      ...bodyCellSx,
                      width: columnWidths.range,
                      textAlign: "right !important",
                      fontSize: 13,
                      fontWeight: 400,
                      ...groupDividerSx,
                      ...getDividerSx(false),
                    }}
                  >
                    <Box sx={{ width: "100%", textAlign: "right", wordBreak: "break-word" }}>
                      {hasNoPack
                        ? "-"
                        : `${getTicketDisplay(item.startingTicketDisplay, item.startingTicket)}-${getTicketDisplay(
                            item.endingTicketDisplay,
                            item.endingTicket
                          )}`}
                    </Box>
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ ...bodyCellSx, ...metricCountCellSx, width: columnWidths.qty, fontSize: 13, fontWeight: 400, ...groupDividerSx, ...getDividerSx(false) }}
                  >
                    <MetricCountValue>{hasNoPack ? "-" : item.qtySold}</MetricCountValue>
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ ...bodyCellSx, ...dollarCellSx, ...metricCellSx, width: columnWidths.value, fontSize: 13, fontWeight: 400, ...groupDividerSx, ...getDividerSx(false) }}
                  >
                    {hasNoPack ? "-" : formatCurrency(item.inventorySoldAmount)}
                  </TableCell>
                  {hasPosType && isFirstInGroup ? (
                    <>
                      <TableCell
                        rowSpan={groupSize}
                        align="right"
                        sx={{ ...bodyCellSx, ...metricCountCellSx, width: columnWidths.qty, fontSize: 13, fontWeight: 400, ...rowSpanDividerSx, ...getDividerSx(false) }}
                      >
                        <MetricCountValue>{item.posQty}</MetricCountValue>
                      </TableCell>
                      <TableCell
                        rowSpan={groupSize}
                        align="right"
                        sx={{ ...bodyCellSx, ...dollarCellSx, ...metricCellSx, width: columnWidths.value, fontSize: 13, fontWeight: 400, ...rowSpanDividerSx, ...getDividerSx(false) }}
                      >
                        {formatCurrency(item.posSales)}
                      </TableCell>
                      <TableCell rowSpan={groupSize} align="right" sx={{ ...bodyCellSx, ...varianceCountCellSx, width: columnWidths.varianceQty, ...rowSpanDividerSx, ...getDividerSx(false) }}>
                        <MetricCountValue>
                          <VarianceValue value={item.quantityVariance} />
                        </MetricCountValue>
                      </TableCell>
                      <TableCell rowSpan={groupSize} align="right" sx={{ ...bodyCellSx, ...metricCellSx, width: columnWidths.varianceValue, ...rowSpanDividerSx, ...getDividerSx(true) }}>
                        <VarianceValue value={item.revenueVariance} currency />
                      </TableCell>
                    </>
                  ) : null}
                </TableRow>
              );
            })
          ) : (
            <TableRow>
              <TableCell
                colSpan={activeColSpan}
                align="center"
                sx={emptyStateCellSx}
              >
                <Box sx={{ minHeight: 80, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  No scratcher data found.
                </Box>
              </TableCell>
            </TableRow>
          )}

          {!isLoading && (isMovedToBackstockView ? movedRows.length > 0 : groupedRows.length > 0) ? (
            <TableRow>
              <TableCell
                align="left"
                sx={{
                  ...totalCellSx,
                  width: columnWidths.game,
                  "&, &&": {
                    textAlign: "left !important",
                  },
                  ...getDividerSx(false),
                }}
              >
                <Box sx={totalLabelSx}>
                  Total
                </Box>
              </TableCell>
              <TableCell
                align="left"
                sx={{
                  ...totalCellSx,
                  width: columnWidths.pack,
                  "&, &&": {
                    textAlign: "left !important",
                  },
                  ...getDividerSx(false),
                }}
              >
                <Box sx={totalPackCountSx}>
                  {`${totalPacksCount.toLocaleString()} ${totalPacksCount === 1 ? "pack" : "packs"}`}
                </Box>
              </TableCell>
              <TableCell sx={{ ...totalCellSx, ...getDividerSx(false) }} />
              <TableCell align="right" sx={{ ...totalCellSx, ...metricCountCellSx, ...getDividerSx(false) }}>
                <MetricCountValue>{totals.qtySold}</MetricCountValue>
              </TableCell>
              <TableCell align="right" sx={{ ...totalCellSx, ...dollarCellSx, ...metricCellSx, ...getDividerSx(false) }}>
                {formatCurrency(totals.soldAmount)}
              </TableCell>
              {!isMovedToBackstockView && hasPosType ? (
                <>
                  <TableCell align="right" sx={{ ...totalCellSx, ...metricCountCellSx, width: columnWidths.qty, ...getDividerSx(false) }}>
                    <MetricCountValue>{totals.scratcherCount}</MetricCountValue>
                  </TableCell>
                  <TableCell align="right" sx={{ ...totalCellSx, ...dollarCellSx, ...metricCellSx, width: columnWidths.value, ...getDividerSx(false) }}>
                    {formatCurrency(totals.scratcherSales)}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      ...totalCellSx,
                      ...varianceCountCellSx,
                      width: columnWidths.varianceQty,
                      ...getDividerSx(false),
                    }}
                  >
                    <MetricCountValue>
                      <VarianceValue value={totals.quantityVariance} />
                    </MetricCountValue>
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      ...totalCellSx,
                      ...metricCellSx,
                      width: columnWidths.varianceValue,
                      ...getDividerSx(true),
                    }}
                  >
                    <VarianceValue value={totals.revenueVariance} currency />
                  </TableCell>
                </>
              ) : null}
              {isMovedToBackstockView ? <TableCell sx={{ ...totalCellSx, ...getDividerSx(true) }} /> : null}
            </TableRow>
          ) : null}
          {!isLoading ? (
            <AuditNotesFootnote
              notes={auditNotes}
              cashierShiftNotes={cashierShiftNotes}
              colSpan={activeColSpan}
            />
          ) : null}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
